import { Sparkles, Settings2 } from "lucide-react";
import type {
  AIModeState,
  AdjustmentsData,
  RankedSpace,
  SpaceAdjustment,
} from "../types";

function getMonthlyPrice(space: RankedSpace): number {
  return space.monthlyPrice ?? space.pricePerWorkstation * space.capacity;
}

function SobConsulta() {
  return (
    <span style={{ color: "#C8C4BE", fontStyle: "italic", fontWeight: 400 }}>
      Sob consulta
    </span>
  );
}

function ComparisonTable({
  spaces,
  adjustments,
  recommendedSpaceIds,
  showLicenseFee = true,
}: {
  spaces: RankedSpace[];
  adjustments: SpaceAdjustment[];
  recommendedSpaceIds?: string[];
  showLicenseFee?: boolean;
}) {
  if (!spaces.length) return null;

  const isRec = (id: string) => recommendedSpaceIds?.includes(id) ?? false;
  const hasAnyRec = spaces.some((s) => isRec(s.id));
  const colTemplate = `160px repeat(${spaces.length}, minmax(150px, 1fr))`;

  const colCellStyle = (spaceId: string): React.CSSProperties => ({
    borderLeft: "1px solid #EDEAE5",
    background: isRec(spaceId) ? "rgba(249,225,11,0.04)" : undefined,
  });

  const sections: Array<{
    label: string;
    rows: Array<{
      key: string;
      label: string;
      render: (
        space: RankedSpace,
        adj: SpaceAdjustment | undefined,
      ) => React.ReactNode;
    }>;
  }> = [
    {
      label: "Valores",
      rows: [
        {
          key: "perPosition",
          label: "R$/posição",
          render: (space, adj) => {
            const mp = getMonthlyPrice(space);
            const v = adj
              ? Math.round(adj.totalPrice / space.capacity)
              : Math.round(mp / space.capacity);
            return (
              <span className="font-semibold" style={{ color: "#1A1814" }}>
                R$ {v.toLocaleString("pt-BR")}
              </span>
            );
          },
        },
        {
          key: "monthly",
          label: "Valor mensal",
          render: (space) => {
            const v = getMonthlyPrice(space);
            return v === 0 ? (
              <SobConsulta />
            ) : (
              `R$ ${v.toLocaleString("pt-BR")}`
            );
          },
        },
        {
          key: "extra",
          label: "Valor extra",
          render: (_, adj) =>
            adj ? `R$ ${adj.extraValue.toLocaleString("pt-BR")}` : "—",
        },
        {
          key: "margin",
          label: "Margem",
          render: (_, adj) => (adj ? `${adj.margin}%` : "—"),
        },
      ],
    },
    {
      label: "Créditos",
      rows: [
        {
          key: "credits",
          label: "Créditos",
          render: (_, adj) => adj?.credits ?? "—",
        },
        {
          key: "extraCredits",
          label: "Créditos extra",
          render: (_, adj) => adj?.extraCredits ?? "—",
        },
      ],
    },
  ];

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        border: "1px solid #E8E6E3",
        background: "#FFFFFF",
        boxShadow:
          "0 1px 4px rgba(26,24,20,0.06), 0 4px 16px rgba(26,24,20,0.04)",
      }}
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: colTemplate,
          borderBottom: "1px solid #EDEAE5",
        }}
      >
        <div className="px-4 py-4" />
        {spaces.map((space) => {
          const rec = isRec(space.id);
          return (
            <div
              key={space.id}
              className="px-3 py-3 text-center"
              style={{
                ...colCellStyle(space.id),
                borderTop: rec ? "3px solid #F9E10B" : "3px solid transparent",
              }}
            >
              {rec && hasAnyRec && (
                <div className="flex justify-center mb-2">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide"
                    style={{ background: "#F9E10B", color: "#1A1814" }}
                  >
                    ★ Recomendado
                  </span>
                </div>
              )}
              <div
                className="font-semibold text-xs leading-snug"
                style={{ color: rec ? "#1A1814" : "#43403D" }}
              >
                {space.roomName}
              </div>
              <div
                className="text-[10px] mt-0.5 leading-snug"
                style={{ color: "#9E9A94" }}
              >
                {space.address}
              </div>
            </div>
          );
        })}
      </div>

      {sections.map((section) => (
        <div key={section.label}>
          <div
            className="grid"
            style={{
              gridTemplateColumns: colTemplate,
              borderTop: "1px solid #F0EEEB",
              borderBottom: "1px solid #F0EEEB",
              background: "#F8F6F2",
            }}
          >
            <div className="px-4 py-1.5">
              <span
                className="text-[9px] font-bold uppercase tracking-widest"
                style={{ color: "#C8C4BE" }}
              >
                {section.label}
              </span>
            </div>
            {spaces.map((space) => (
              <div
                key={space.id}
                style={{
                  borderLeft: "1px solid #EDEAE5",
                  background: isRec(space.id)
                    ? "rgba(249,225,11,0.04)"
                    : "#F8F6F2",
                }}
              />
            ))}
          </div>

          {section.rows.map((row, i) => (
            <div
              key={row.key}
              className="grid"
              style={{
                gridTemplateColumns: colTemplate,
                borderBottom:
                  i < section.rows.length - 1 ? "1px solid #F5F3F0" : undefined,
              }}
            >
              <div
                className="px-4 py-2.5 flex items-center"
                style={{ color: "#78736D", minWidth: 0 }}
              >
                <span className="text-[11px]">{row.label}</span>
              </div>
              {spaces.map((space) => {
                const adj = adjustments.find((a) => a.spaceId === space.id);
                return (
                  <div
                    key={space.id}
                    className="px-3 py-2.5 flex items-center justify-center text-[11px]"
                    style={{ ...colCellStyle(space.id), color: "#43403D" }}
                  >
                    {row.render(space, adj)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}

      <div
        className="grid"
        style={{
          gridTemplateColumns: colTemplate,
          borderTop: "1.5px solid #E8E6E3",
          background: "#FAFAF8",
        }}
      >
        <div className="px-4 py-3.5 flex items-center">
          <span className="text-xs font-bold" style={{ color: "#1A1814" }}>
            Total
          </span>
        </div>
        {spaces.map((space) => {
          const adj = adjustments.find((a) => a.spaceId === space.id);
          const mp = getMonthlyPrice(space);
          const rec = isRec(space.id);
          const total = adj
            ? adj.totalPrice + (showLicenseFee ? (adj.setupCost ?? 0) : 0)
            : mp === 0
              ? null
              : mp;
          return (
            <div
              key={space.id}
              className="px-3 py-3.5 flex items-center justify-center"
              style={{
                borderLeft: "1px solid #EDEAE5",
                background: rec ? "rgba(249,225,11,0.06)" : undefined,
              }}
            >
              <span
                className="text-sm font-bold"
                style={{ color: rec ? "#1A1814" : "#43403D" }}
              >
                {total === null ? (
                  <SobConsulta />
                ) : (
                  `R$ ${total.toLocaleString("pt-BR")}`
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface AdjustmentsPreviewProps {
  state: AIModeState;
  liveAdjustments: AdjustmentsData | null;
}

export default function AdjustmentsPreview({
  state,
  liveAdjustments,
}: AdjustmentsPreviewProps) {
  const adj = liveAdjustments ?? state.adjustments;
  const selectedSpaceIds = state.location?.selectedSpaceIds ?? [];
  const selectedComboIds = state.location?.selectedComboIds ?? [];
  const roomCombinations = state.location?.roomCombinations ?? [];
  const allSpaces = [
    ...(state.location?.rankedSpaces ?? []),
    ...(state.location?.extraSpaces ?? []),
  ];
  // Proposal rooms = individually-selected ∪ rooms of the selected combos, so the
  // comparison mirrors the Ajustes panel instead of dropping combo-only rooms.
  const proposalRoomIds = new Set(selectedSpaceIds);
  for (const combo of roomCombinations) {
    if (!selectedComboIds.includes(combo.combinationId)) continue;
    for (const r of combo.rooms) proposalRoomIds.add(r.privateRoomId);
  }
  const selectedSpaces = allSpaces.filter((s) => proposalRoomIds.has(s.id));
  const recommendedSpaceIds = adj?.recommendedSpaceIds ?? [];
  const recCount = selectedSpaces.filter((s) =>
    recommendedSpaceIds.includes(s.id),
  ).length;

  return (
    <div
      className="w-full h-full flex flex-col overflow-y-auto ai-wizard-scroll"
      style={{
        background: "linear-gradient(160deg, #FDFCF8 0%, #F8F5EE 100%)",
      }}
    >
      <div
        className="flex-shrink-0 px-6 pt-6 pb-4"
        style={{ borderBottom: "1px solid #EDEAE5" }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{
              background: "linear-gradient(135deg, #FFD900 0%, #F5C800 100%)",
              boxShadow: "0 2px 8px rgba(249,225,11,0.35)",
            }}
          >
            <Sparkles className="w-4 h-4" style={{ color: "#1A1814" }} />
          </div>
          <div>
            <div className="text-sm font-bold" style={{ color: "#1A1814" }}>
              Comparativo da Proposta
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px]" style={{ color: "#9E9A94" }}>
                {selectedSpaces.length} sala
                {selectedSpaces.length !== 1 ? "s" : ""}
              </span>
              {recCount > 0 && (
                <>
                  <span style={{ color: "#D8D4CE" }}>·</span>
                  <span
                    className="text-[11px] font-medium"
                    style={{ color: "#B09800" }}
                  >
                    {recCount} recomendada{recCount !== 1 ? "s" : ""}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-x-auto">
        {selectedSpaces.length > 0 ? (
          <div style={{ minWidth: `${160 + selectedSpaces.length * 150}px` }}>
            <ComparisonTable
              spaces={selectedSpaces}
              adjustments={adj?.spaceAdjustments ?? []}
              recommendedSpaceIds={recommendedSpaceIds}
              showLicenseFee={adj?.showLicenseFee ?? true}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-20 gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center"
              style={{ background: "#F0EEEB" }}
            >
              <Settings2 className="w-5 h-5" style={{ color: "#C8C4BE" }} />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium" style={{ color: "#78736D" }}>
                Nenhuma sala selecionada
              </p>
              <p className="text-xs mt-1" style={{ color: "#B8B4AE" }}>
                Configure as preferências da proposta
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
